import React, { useState, useEffect } from 'react';
import { useAlert } from './CustomAlert/AlertContext';
import { useParams, useNavigate } from 'react-router-dom';
import ScreenshotGallery from './ScreenshotGallery';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PlaybackViewer from './interview/PlaybackViewer';
import { API_BASE } from '../utils/config';

const ResultsView = () => {
  const { showAlert } = useAlert();

  const { roomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false); // 🆕 Specific loading state
    const [autoReportAttempted, setAutoReportAttempted] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hasReplay, setHasReplay] = useState(false);
  const [replayMetadata, setReplayMetadata] = useState(null);
  const [showReplayModal, setShowReplayModal] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        // Correct endpoint: /api/auth/interviews/:roomId/details
        const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/details`);
        if (!res.ok) throw new Error(`Failed to fetch details: ${res.status}`);
        const result = await res.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [roomId]);

  useEffect(() => {
    const checkReplayData = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/playback/${roomId}/metadata`);
        if (res.ok) {
          const result = await res.json();
          if (result.success && result.metadata && result.metadata.totalEvents > 0) {
            setHasReplay(true);
            setReplayMetadata(result.metadata);
          }
        }
      } catch (err) {
        console.warn('⚠️ Failed to fetch playback metadata:', err);
      }
    };
    if (roomId) checkReplayData();
  }, [roomId]);

    useEffect(() => {
        const autoGenerateReport = async () => {
            if (!data || generatingReport || autoReportAttempted) return;

            const existingReport = data.room?.ai_report_json;
            const hasCleanedTranscript = Boolean(data.room?.cleaned_transcript);
            const hasRawTranscript = Boolean(
                data.transcript &&
                String(data.transcript).trim() &&
                data.transcript !== 'No transcript generated yet.'
            );

            if (existingReport || (!hasCleanedTranscript && !hasRawTranscript)) return;

            try {
                setAutoReportAttempted(true);
                setGeneratingReport(true);

                const reportRes = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/report`, {
                    method: 'POST'
                });
                const reportResult = await reportRes.json();

                if (!reportRes.ok || !reportResult.success) {
                    throw new Error(reportResult.error || `Failed to generate report: ${reportRes.status}`);
                }

                const detailsRes = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/details`);
                if (!detailsRes.ok) {
                    throw new Error(`Failed to refresh details: ${detailsRes.status}`);
                }

                const refreshed = await detailsRes.json();
                setData(refreshed);
            } catch (err) {
                console.error('Auto AI report generation failed:', err);
            } finally {
                setGeneratingReport(false);
            }
        };

        autoGenerateReport();
    }, [data, roomId, generatingReport, autoReportAttempted]);

  if (loading) return <div style={styles.center}>Loading results...</div>;
  if (error) return <div style={styles.center}>Error: {error}</div>;
  if (!data) return <div style={styles.center}>No data found</div>;

    const { room, transcript } = data;
    const hasTranscriptContent = Boolean(
        transcript &&
        String(transcript).trim() &&
        transcript !== 'No transcript generated yet.'
    );
    const showScreenshotRoleFilters = room?.interview_type !== 'AI';

  return (

    
    <div style={styles.pageWrapper}>
            <Navbar isLoggedIn userName={JSON.parse(localStorage.getItem('user') || '{}')?.full_name} />
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate('/hr-dashboard/interviews?tab=rooms')} style={styles.backButton}>
            ← Back to Active Rooms
          </button>
          <h1 style={styles.title}>Interview Results</h1>
          <div style={{ width: 100 }}></div>
        </div>
        <div style={styles.card}>
          {/* <h2 style={styles.sectionTitle}>Details</h2> */}
          <div style={styles.grid}>
            <div><strong>Room ID:</strong> {room.room_id}</div>
            <div><strong>Candidate:</strong> {room.candidate_name || 'N/A'}</div>
            <div><strong>Date:</strong> {new Date(String(room.scheduled_date).replace('Z', '')).toLocaleString()}</div>
            <div><strong>Status:</strong> <span style={styles.status(room.status)}>{room.status}</span></div>
          </div>
        </div>

        {/* CERTIFIED INTERVIEWER EVALUATION SCORECARD */}
        {data.evaluation ? (
          <div style={styles.evaluationCard}>
            <div style={styles.evaluationHeader}>
              <div style={styles.evalHeaderTitleGroup}>
                <div style={styles.evalAvatarIcon}>👨‍💼</div>
                <div>
                  <h2 style={styles.evalCardTitle}>Certified Interviewer Assessment Scorecard</h2>
                  <p style={styles.evalCardSubtitle}>
                    Conducted by <strong>{data.evaluation.interviewer_name || 'Assigned Interviewer'}</strong>
                    {data.evaluation.interviewer_email && ` (${data.evaluation.interviewer_email})`}
                    {data.evaluation.evaluated_at && ` • Evaluated on ${new Date(data.evaluation.evaluated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  </p>
                </div>
              </div>

              <div style={styles.evalHeaderBadges}>
                {data.evaluation.round_type && (
                  <span style={styles.roundBadge}>
                    💻 {data.evaluation.round_type.replace('_', ' ')}
                  </span>
                )}
                {data.evaluation.job_title && (
                  <span style={styles.jobBadge}>
                    💼 {data.evaluation.job_title}
                  </span>
                )}
              </div>
            </div>

            <div style={styles.evalMetricsGrid}>
              {/* Overall Score */}
              <div style={styles.evalMetricBox}>
                <div style={styles.metricLabel}>Overall Recommendation</div>
                <div style={styles.overallScoreDisplay}>
                  <span style={styles.scoreNumber}>{data.evaluation.overall_rating || 5}.0</span>
                  <span style={styles.scoreMax}>/ 5.0</span>
                </div>
                <div style={styles.starsRow}>
                  {'⭐'.repeat(Number(data.evaluation.overall_rating) || 5)}
                </div>
                <span style={styles.recommendationPill}>
                  ✓ Recommended for Next Step / Hire
                </span>
              </div>

              {/* Technical Score */}
              <div style={styles.evalMetricBox}>
                <div style={styles.metricLabel}>Technical Competency</div>
                <div style={styles.metricScoreNum}>
                  <strong>{data.evaluation.technical_rating || 5}</strong> / 5
                </div>
                <div style={styles.scoreBarTrack}>
                  <div style={{ ...styles.scoreBarFill, width: `${((data.evaluation.technical_rating || 5) / 5) * 100}%`, backgroundColor: '#2563eb' }} />
                </div>
                <span style={styles.metricHint}>Coding, architecture & problem solving</span>
              </div>

              {/* Communication Score */}
              <div style={styles.evalMetricBox}>
                <div style={styles.metricLabel}>Communication & Culture</div>
                <div style={styles.metricScoreNum}>
                  <strong>{data.evaluation.communication_rating || 4}</strong> / 5
                </div>
                <div style={styles.scoreBarTrack}>
                  <div style={{ ...styles.scoreBarFill, width: `${((data.evaluation.communication_rating || 4) / 5) * 100}%`, backgroundColor: '#10b981' }} />
                </div>
                <span style={styles.metricHint}>Articulation, collaboration & clarity</span>
              </div>
            </div>

            {/* Qualitative Feedback Notes */}
            {data.evaluation.interviewer_notes && (
              <div style={styles.evalNotesBox}>
                <strong style={styles.notesBoxHeading}>💬 Interviewer Observations & Qualitative Feedback:</strong>
                <p style={styles.notesBoxContent}>
                  "{data.evaluation.interviewer_notes}"
                </p>
              </div>
            )}

            {/* Next Steps Quick Actions */}
            <div style={styles.evalActionsRow}>
              <button
                onClick={() => navigate('/hr-dashboard/booked-candidates')}
                style={styles.evalActionBtnPrimary}
              >
                📋 View in Booked Candidates Roster
              </button>
              <button
                onClick={() => navigate('/hr-dashboard/pipeline')}
                style={styles.evalActionBtnSecondary}
              >
                📊 View in Candidate Pipeline
              </button>
            </div>
          </div>
        ) : null}

        {/* Coding Replay Session Section */}
        {hasReplay && (
          <div style={styles.replayCard}>
            <div style={styles.replayCardInfo}>
              <h2 style={{...styles.sectionTitle, marginBottom: '6px'}}>💻 Candidate Coding Session Replay</h2>
              <p style={styles.replayDescription}>
                Watch the candidate's exact code construction process and coding speed keystroke-by-keystroke.
                {replayMetadata && (
                  <span style={styles.replayStats}>
                    {` (${replayMetadata.totalEvents} events recorded over ${Math.max(1, Math.round((replayMetadata.totalDurationMs || 0) / 60000))} mins)`}
                  </span>
                )}
              </p>
            </div>
            <button onClick={() => setShowReplayModal(true)} style={styles.watchReplayBtn}>
              🎬 Watch Replay
            </button>
          </div>
        )}

        {/* TWO BIG COLUMNS */}
        <div style={styles.twoColumn}>
            
            {/* Left Column: Cleaned Q&A Section */}
            <div style={styles.column}>
                <h3 style={styles.colTitle}>Cleaned Q&A Transcript</h3>
                {(() => {
                    // 1. Try to load AI Cleaned Transcript
                    let cleanedData = null;
                    try {
                        if (room.cleaned_transcript) {
                            cleanedData = typeof room.cleaned_transcript === 'string' 
                                          ? JSON.parse(room.cleaned_transcript) 
                                          : room.cleaned_transcript;
                        }
                    } catch (e) {
                         console.error("Error parsing cleaned transcript", e);
                    }

                    if (cleanedData && cleanedData.conversation && cleanedData.conversation.length > 0) {
                        return (
                            <div style={styles.list}>
                                {cleanedData.conversation.map((msg, idx) => (
                                    <div key={idx} style={{...styles.item, borderLeft: (msg.speaker || '').toUpperCase() === 'INTERVIEWER' ? '4px solid #1976d2' : '4px solid #388e3c'}}>
                                        <div style={{...styles.qText, color: (msg.speaker || '').toUpperCase() === 'INTERVIEWER' ? '#1976d2' : '#388e3c', fontWeight: 'bold' }}>
                                            {msg.speaker}:
                                        </div>
                                        <div style={{...styles.aText, marginTop: '4px'}}>{msg.text}</div>
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    // 2. Fallback to combined transcript with speaker labels
                    return transcript ? (
                        <div style={styles.list}>
                            {transcript
                                .split('\n\n')
                                .map(line => String(line || '').trim())
                                .filter(line => line)
                                .filter(line => !line.toUpperCase().includes('=== COMPLETE INTERVIEW TRANSCRIPT ==='))
                                .map((line, idx) => {
                                const textMatch = line.indexOf(': ');
                                const rawSpeaker = textMatch > -1 ? line.substring(0, textMatch).trim() : '';
                                const upperSpeaker = rawSpeaker.toUpperCase();

                                let speaker = 'CANDIDATE';
                                if (upperSpeaker.includes('INTERVIEWER')) speaker = 'INTERVIEWER';
                                else if (upperSpeaker.includes('CANDIDATE')) speaker = 'CANDIDATE';

                                const isInterviewer = speaker === 'INTERVIEWER';
                                const text = textMatch > -1 ? line.substring(textMatch + 2).trim() : line;

                                return (
                                    <div key={idx} style={{...styles.item, borderLeft: isInterviewer ? '4px solid #1976d2' : '4px solid #388e3c'}}>
                                        <div style={{...styles.qText, color: isInterviewer ? '#1976d2' : '#388e3c', fontWeight: 'bold' }}>
                                            {speaker}:
                                        </div>
                                        <div style={{...styles.aText, marginTop: '4px'}}>{text}</div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p>No Cleaned Transcript or Q&A recorded yet.</p>
                    );
                })()}
            </div>

            {/* Right Column: AI Executive Summary Section */}
            <div style={styles.column}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px'}}>
                    <h3 style={{...styles.colTitle, marginBottom:0, borderBottom:'none', paddingBottom:0}}>AI Executive Summary</h3>
                    {!room.ai_report_json && !generatingReport && hasTranscriptContent && (
                        <button 
                            onClick={async () => {
                                try {
                                    setGeneratingReport(true);
                                    const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/report`, { method: 'POST' });
                                    const result = await res.json();
                                    if (result.success) {
                                        window.location.reload();
                                    } else {
                                        showAlert('Failed: ' + result.error, 'error');
                                        setGeneratingReport(false);
                                    }
                                } catch (e) {
                                    showAlert('Error: ' + e.message, 'error');
                                    setGeneratingReport(false);
                                }
                            }}
                            style={styles.generateBtn}
                        >
                            ✨ Generate AI Report
                        </button>
                    )}
                </div>

                {generatingReport ? (
                     <div style={styles.skeletonContainer}>
                        <div style={styles.skeletonScore}></div>
                        <div style={{flex:1}}>
                            <div style={styles.skeletonText(80)}></div>
                            <div style={styles.skeletonText(90)}></div>
                            <div style={styles.skeletonText(60)}></div>
                            <div style={{marginTop: 16, display:'flex', gap: 8}}>
                                 <div style={styles.skeletonTag}></div>
                                 <div style={styles.skeletonTag}></div>
                                 <div style={styles.skeletonTag}></div>
                            </div>
                        </div>
                     </div>
                ) : room.ai_report_json ? (
                    (() => {
                        let report = null;
                        try { report = JSON.parse(room.ai_report_json); } catch(e) {}
                        
                        if (!report) return <p>Error loading report.</p>;

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                                    <div style={styles.scoreBox}>
                                        <div style={styles.scoreCircle(report.score)}>
                                            {report.score}
                                        </div>
                                        <div style={styles.recLabel}>Overall Score</div>
                                    </div>
                                    
                                    <div style={{ flex: 1 }}>
                                        <p style={{...styles.summaryText, fontSize: '16px', lineHeight: '1.7'}}>{report.summary}</p>
                                    </div>
                                </div>
                                
                                <div style={{...styles.reportContent, display: 'flex', gap: '24px'}}>
                                    <div style={{flex: 1}}>
                                        <strong style={{ display: 'block', width: '100%', marginBottom: '12px', fontSize: '16px' }}>Strengths ✨</strong>
                                        <div style={{...styles.tagGroup, flexDirection: 'column', alignItems: 'flex-start'}}>
                                            {(report.strengths || []).map((s, i) => (
                                                <span key={i} style={styles.tagGreen}>check {s}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()
                ) : (
                    <p style={{color:'#64748b', fontStyle:'italic', marginTop: '16px'}}>
                        No executive summary generated. Transcripts not available.
                    </p>
                )}
            </div>
        </div>

        {/* NEW Proctoring Integrity Section */}
        {room.interview_type === 'AI' && (
            <div style={{...styles.card, marginTop: '24px'}}>
              <h2 style={{...styles.sectionTitle, color: '#b91c1c'}}>Proctoring Integrity Report</h2>
              <div style={styles.grid}>
                {/* <div>
                   <strong>Gaze Warnings:</strong> 
                   <div style={{ marginTop: '4px', color: room.warning_count > 0 ? '#b91c1c' : '#166534', fontWeight: 500 }}>
                     The candidate was verified looking away {room.warning_count || 0} times.
                   </div>
                </div> */}
                <div>
                   <strong>Lip-Sync Verification:</strong>
                   {(() => {
                      let sync = { success: 0, total: 0 };
                      try {
                        if (room.lip_sync_stats) {
                           sync = typeof room.lip_sync_stats === 'string' ? JSON.parse(room.lip_sync_stats) : room.lip_sync_stats;
                        }
                      } catch (e) {}
                      return (
                         <div style={{ marginTop: '4px', color: sync.success === sync.total && sync.total > 0 ? '#166534' : (sync.total > 0 ? '#b91c1c' : '#64748b'), fontWeight: 500 }}>
                            Background verification checks passed {sync.success}/{sync.total} times.
                         </div>
                      );
                   })()}
                </div>
              </div>
            </div>
        )}

        {/* Screenshot Gallery Section */}
        <div style={{ marginTop: '24px' }}>
                    <ScreenshotGallery interviewId={roomId} viewMode="grid" showRoleFilters={showScreenshotRoleFilters} />
        </div>

      </div>

      {/* Full-Screen Replay Overlay Modal */}
      {showReplayModal && (
        <div style={styles.modalOverlay} onClick={() => setShowReplayModal(false)}>
          <div style={styles.modalContentLarge} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>🎬 Candidate Coding Playback</h2>
              <button onClick={() => setShowReplayModal(false)} style={styles.modalCloseBtn}>
                ✕ Close
              </button>
            </div>
            <div style={styles.modalBody}>
              <PlaybackViewer roomId={roomId} onClose={() => setShowReplayModal(false)} />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

const styles = {
  pageWrapper: {
    minHeight: 'calc(100vh - 72px)',
    background: 'var(--bg-page)',
    padding: '40px 20px 60px',
    fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  },
  container: {
    width: '100%',
    maxWidth: '1500px',
    margin: '0 auto',
  },
  center: {
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    color: 'var(--text-muted)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  backButton: {
    background: 'var(--bg-card-glass)',
    border: '1px solid var(--border-color)',
    padding: '10px 20px',
    borderRadius: '10px',
    cursor: 'pointer',
    color: 'var(--text-tertiary)',
    fontWeight: '600',
    fontFamily: `'Inter', sans-serif`,
    boxShadow: 'var(--shadow-sm)',
    transition: 'all 0.2s',
  },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: 'var(--text-primary)',
    fontFamily: `'Outfit', sans-serif`,
    margin: 0,
    letterSpacing: '0.2px',
  },
  card: {
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding: '24px',
    borderRadius: '16px',
    border: '1px solid var(--border-card)',
    boxShadow: 'var(--shadow-card)',
    marginBottom: '24px',
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '20px',
    color: 'var(--text-primary)',
    fontFamily: `'Outfit', sans-serif`,
    fontWeight: '700',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: '20px',
    color: 'var(--text-secondary)',
  },
  status: (status) => ({
    padding: '6px 12px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '700',
    backgroundColor: status === 'COMPLETED' ? '#dcfce7' : '#fef3c7',
    color: status === 'COMPLETED' ? '#166534' : '#b45309',
    border: `1px solid ${status === 'COMPLETED' ? '#bbf7d0' : '#fde68a'}`,
  }),
  twoColumn: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gap: '24px',
      alignItems: 'start',
  },
  column: {
      background: 'var(--bg-card-glass)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      padding: '24px',
      borderRadius: '16px',
      border: '1px solid var(--border-card)',
      boxShadow: 'var(--shadow-card)',
  },
  colTitle: {
      marginTop: 0,
      borderBottom: '2px solid var(--border-color)',
      paddingBottom: '12px',
      marginBottom: '16px',
      color: 'var(--text-primary)',
      fontFamily: `'Outfit', sans-serif`,
      fontSize: '20px',
  },
  list: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxHeight: '600px', // ✅ Added scroll
      overflowY: 'auto',  // ✅ Added scroll
      paddingRight: '8px', // prevent scrollbar overlap
  },
  item: {
      padding: '16px',
      backgroundColor: 'var(--bg-surface)',
      borderRadius: '12px',
      border: '1px solid var(--border-color)',
  },
  qText: {
      marginBottom: '6px',
      color: 'var(--text-secondary)',
  },
  aText: {
      marginBottom: '8px',
      color: 'var(--text-primary)',
      lineHeight: '1.6',
  },
  meta: {
      fontSize: '12px',
      color: 'var(--text-muted)',
      display: 'flex',
      gap: '12px',
  },
  transcriptBox: {
      whiteSpace: 'pre-wrap',
      lineHeight: '1.6',
      color: 'var(--text-secondary)',
      maxHeight: '500px',
      overflowY: 'auto',
  },
  generateBtn: {
      background: 'linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '10px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '14px',
      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
      transition: 'all 0.2s',
  },
  reportGrid: {
      display: 'flex',
      gap: '32px',
      alignItems: 'flex-start',
  },
  scoreBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      minWidth: '120px',
  },
  scoreCircle: (score) => ({
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '28px',
      fontWeight: '800',
      fontFamily: `'Outfit', sans-serif`,
      backgroundColor: score >= 70 ? '#dcfce7' : score >= 40 ? '#fef3c7' : '#fee2e2',
      color: score >= 70 ? '#166534' : score >= 40 ? '#b45309' : '#b91c1c',
      border: `4px solid ${score >= 70 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444'}`,
      boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
  }),
  recLabel: {
      fontSize: '14px',
      fontWeight: '700',
      color: 'var(--text-primary)',
      textAlign: 'center',
  },
  reportContent: {
      flex: 1,
  },
  summaryText: {
      lineHeight: '1.6',
      color: 'var(--text-secondary)',
      marginBottom: '16px',
      fontSize: '15px',
      background: 'var(--bg-surface)',
      padding: '16px',
      borderRadius: '12px',
      border: '1px solid var(--border-color)'
  },
  tagGroup: {
      marginBottom: '12px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      alignItems: 'center',
      fontSize: '14px',
  },
  tagGreen: {
      backgroundColor: '#f0fdf4',
      color: '#166534',
      padding: '6px 12px',
      borderRadius: '8px',
      fontWeight: '600',
      border: '1px solid #bbf7d0',
  },
  tagRed: {
      backgroundColor: '#fef2f2',
      color: '#991b1b',
      padding: '6px 12px',
      borderRadius: '8px',
      fontWeight: '600',
      border: '1px solid #fecaca',
  },
  // Skeleton Styles
  skeletonContainer: {
      display: 'flex',
      gap: '32px',
      alignItems: 'flex-start',
      animation: 'pulse 1.5s infinite ease-in-out',
  },
  skeletonScore: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: 'rgba(226, 232, 240, 0.8)',
  },
  skeletonText: (widthPc) => ({
      height: '16px',
      width: `${widthPc}%`,
      backgroundColor: 'rgba(226, 232, 240, 0.8)',
      marginBottom: '10px',
      borderRadius: '6px',
  }),
  skeletonTag: {
      width: '80px',
      height: '32px',
      backgroundColor: 'rgba(226, 232, 240, 0.8)',
      borderRadius: '8px',
  },
  // Replay Styles
  replayCard: {
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding: '24px',
    borderRadius: '16px',
    border: '1px solid var(--border-card)',
    boxShadow: 'var(--shadow-card)',
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  replayCardInfo: {
    flex: 1,
    minWidth: '280px',
  },
  replayDescription: {
    margin: 0,
    color: 'var(--text-secondary)',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  replayStats: {
    fontWeight: '600',
    color: '#f59e0b',
  },
  watchReplayBtn: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '15px',
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
    transition: 'all 0.2s',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    backdropFilter: 'blur(8px)',
  },
  modalContentLarge: {
    width: '90%',
    maxWidth: '1200px',
    height: '85vh',
    backgroundColor: '#1e1e1e',
    borderRadius: '16px',
    border: '1px solid #333',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #333',
    backgroundColor: '#252526',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#fff',
    fontWeight: '600',
    fontFamily: `'Outfit', sans-serif`,
  },
  modalCloseBtn: {
    background: 'transparent',
    border: 'none',
    color: '#aaa',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'color 0.2s',
  },
  modalBody: {
    flex: 1,
    padding: '16px',
    minHeight: 0,
    backgroundColor: '#1e1e1e',
  },
  evaluationCard: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
    marginBottom: '24px',
    textAlign: 'left'
  },
  evaluationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
    paddingBottom: '18px',
    borderBottom: '1px solid #f1f5f9'
  },
  evalHeaderTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  evalAvatarIcon: {
    fontSize: '32px',
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    backgroundColor: '#eff6ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  evalCardTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '800',
    color: '#0f172a',
    fontFamily: `'Outfit', sans-serif`,
    letterSpacing: '-0.3px'
  },
  evalCardSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '13.5px',
    color: '#64748b'
  },
  evalHeaderBadges: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  roundBadge: {
    padding: '4px 10px',
    borderRadius: '6px',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
    fontSize: '12px',
    fontWeight: '700'
  },
  jobBadge: {
    padding: '4px 10px',
    borderRadius: '6px',
    backgroundColor: '#f1f5f9',
    color: '#334155',
    border: '1px solid #e2e8f0',
    fontSize: '12px',
    fontWeight: '600'
  },
  evalMetricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginTop: '20px'
  },
  evalMetricBox: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  metricLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  overallScoreDisplay: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px'
  },
  scoreNumber: {
    fontSize: '32px',
    fontWeight: '900',
    color: '#059669',
    lineHeight: '1'
  },
  scoreMax: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#94a3b8'
  },
  starsRow: {
    fontSize: '16px',
    letterSpacing: '2px',
    margin: '2px 0'
  },
  recommendationPill: {
    display: 'inline-block',
    fontSize: '11.5px',
    fontWeight: '700',
    color: '#065f46',
    backgroundColor: '#dcfce7',
    padding: '3px 8px',
    borderRadius: '6px',
    marginTop: '4px',
    width: 'fit-content'
  },
  metricScoreNum: {
    fontSize: '20px',
    color: '#0f172a',
    marginTop: '2px'
  },
  scoreBarTrack: {
    width: '100%',
    height: '6px',
    backgroundColor: '#e2e8f0',
    borderRadius: '999px',
    overflow: 'hidden',
    margin: '4px 0'
  },
  scoreBarFill: {
    height: '100%',
    borderRadius: '999px'
  },
  metricHint: {
    fontSize: '11.5px',
    color: '#64748b'
  },
  evalNotesBox: {
    marginTop: '20px',
    padding: '16px 18px',
    borderRadius: '12px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0'
  },
  notesBoxHeading: {
    display: 'block',
    fontSize: '13px',
    color: '#166534',
    marginBottom: '6px'
  },
  notesBoxContent: {
    margin: 0,
    fontSize: '14px',
    color: '#14532d',
    lineHeight: '1.6',
    fontStyle: 'italic'
  },
  evalActionsRow: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
    flexWrap: 'wrap'
  },
  evalActionBtnPrimary: {
    padding: '9px 18px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  evalActionBtnSecondary: {
    padding: '9px 18px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    backgroundColor: '#ffffff',
    color: '#334155',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  }
};

// Add global styles for animation via style tag hack or just rely on a CSS file?
// Since we have inline styles, let's inject a style tag for the keyframes
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;
document.head.appendChild(styleSheet);

export default ResultsView;
